import React, { useState } from 'react';
import { X, ZoomIn } from 'lucide-react';

const libelleVue = (url) => (/chassis|telaio/i.test(String(url || '')) ? 'Châssis' : 'Carrosserie');

// Vue éclatée officielle : on survole une pastille pour voir la pièce, on clique sur la
// photo pour afficher l'ensemble en grand.
export default function PlanEclate({ product }) {
  const vues = (Array.isArray(product?.plan_eclate) ? product.plan_eclate : [])
    .filter((v) => v?.image_url && Array.isArray(v.hotspots) && v.hotspots.length);
  const [index, setIndex] = useState(0);
  const [actif, setActif] = useState(null);
  const [zoom, setZoom] = useState(false);

  if (!vues.length) return null;
  const vue = vues[Math.min(index, vues.length - 1)];

  const Pastille = ({ spot, i }) => {
    const ouvert = actif === i;
    return (
      <div className="absolute -translate-x-1/2 -translate-y-1/2 z-10" style={{ left: `${spot.x}%`, top: `${spot.y}%` }}>
        <button
          type="button"
          aria-label={`Pièce ${spot.refs.join(', ') || 'de la vue éclatée'}`}
          onMouseEnter={() => setActif(i)}
          onMouseLeave={() => setActif(null)}
          onClick={() => setActif(ouvert ? null : i)}
          className="block w-6 h-6 rounded-full bg-cs-blue ring-2 ring-white shadow-md hover:bg-cs-blue-dark focus:outline-none focus:ring-4 focus:ring-cs-blue/40"
        />
        {ouvert && (
          <div className={`absolute top-1/2 -translate-y-1/2 z-20 w-40 bg-white border border-gray-900 p-2 text-center ${spot.x > 55 ? 'right-full mr-3' : 'left-full ml-3'}`}>
            {spot.refs.length > 0 && (
              <p className="text-xs font-bold text-gray-900 break-words leading-tight">{spot.refs.join(' · ')}</p>
            )}
            {spot.image_url && (
              <img src={spot.image_url} alt={spot.refs.join(', ')} loading="lazy" decoding="async" className="mt-1 w-full max-h-28 object-contain" />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mt-16 pt-10 border-t border-gray-100">
      <h2 className="font-display font-bold text-xl text-gray-900">VUE ÉCLATÉE</h2>
      <p className="text-sm text-gray-500 mb-4">
        Survolez une pastille pour voir la pièce correspondante, cliquez sur la photo pour afficher l'ensemble en grand.
      </p>

      {vues.length > 1 && (
        <div className="flex gap-2 mb-3">
          {vues.map((v, i) => (
            <button
              key={i}
              type="button"
              onClick={() => { setIndex(i); setActif(null); }}
              className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${i === index ? 'bg-cs-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {libelleVue(v.image_url).toUpperCase()}
            </button>
          ))}
        </div>
      )}

      <div className="relative bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
        <button type="button" onClick={() => setZoom(true)} aria-label="Afficher l'ensemble en grand" className="block w-full">
          <img src={vue.image_url} alt={`Vue éclatée ${product.name}`} className="w-full h-auto" loading="lazy" decoding="async" />
        </button>
        {vue.hotspots.map((spot, i) => <Pastille key={i} spot={spot} i={i} />)}
        <span className="absolute bottom-2 right-2 flex items-center gap-1 bg-white/90 border border-gray-200 rounded px-2 py-1 text-[10px] font-bold text-gray-500">
          <ZoomIn className="w-3 h-3" /> AGRANDIR
        </span>
      </div>

      {zoom && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setZoom(false)}>
          <button type="button" onClick={() => setZoom(false)} aria-label="Fermer" className="absolute top-4 right-4 text-white/80 hover:text-white">
            <X className="w-8 h-8" />
          </button>
          <div className="relative max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={vue.image_url} alt={`Vue éclatée ${product.name}`} className="w-full h-auto max-h-[88vh] object-contain" />
            {vue.hotspots.map((spot, i) => <Pastille key={i} spot={spot} i={i} />)}
          </div>
        </div>
      )}
    </div>
  );
}